// utils/productDefaults.js
// Default product data generator and constants
// ALIGNED with ProductForm DEFAULT_PRODUCT_FORM_VALUES

// ============= ALIGNED CONSTANTS FROM PRODUCTFORM =============
export const PRODUCT_TYPE_ENUM = [
  "GOLD",
  "SILVER",
  "PLATINUM",
  "DIAMOND",
  "STUDDED",
];

export const PRODUCT_STATUS_ENUM = ["ACTIVE", "INACTIVE", "DRAFT", "ARCHIVED"];

export const PRODUCT_GENDER_ENUM = ["MALE", "FEMALE", "UNISEX", "KIDS"];

export const VIEW_ACCESS_ENUM = [
  "B2C_APP",
  "B2C_WEBSITE",
  "B2B_WEBSITE",
  "B2B_APP",
];

export const PURITY_ENUM = ["24K", "22K", "18K", "14K", "925", "950", "PT950"];

export const METAL_COLOR_ENUM = [
  "YELLOW",
  "WHITE",
  "ROSE",
  "TWO_TONE",
  "THREE_TONE",
];

export const PRODUCT_SUBTYPE_ENUM = [
  "RING",
  "NECKLACE",
  "EARRING",
  "BRACELET",
  "PENDANT",
  "CHAIN",
  "BANGLE",
  "ANKLET",
  "NOSE_PIN",
  "MANGALSUTRA",
  "KADA",
  "COIN",
  "BAR",
  "IDOL",
  "OTHER",
];

export const CRAFT_TYPE_ENUM = [
  "HANDMADE",
  "MACHINE_MADE",
  "CAST",
  "HANDCRAFTED",
  "STAMPED",
];

export const TECHNIQUE_ENUM = [
  "KUNDAN",
  "MEENAKARI",
  "TEMPLE",
  "FILIGREE",
  "ANTIQUE",
  "MODERN",
  "TRADITIONAL",
];

export const STONE_TYPE_ENUM = [
  "DIAMOND",
  "RUBY",
  "EMERALD",
  "SAPPHIRE",
  "PEARL",
  "TOPAZ",
  "AMETHYST",
  "OTHER",
];

export const STONE_SHAPE_ENUM = [
  "ROUND",
  "PRINCESS",
  "OVAL",
  "MARQUISE",
  "PEAR",
  "CUSHION",
  "EMERALD",
  "ASSCHER",
  "RADIANT",
  "HEART",
];

export const STONE_SETTING_ENUM = [
  "PRONG",
  "BEZEL",
  "CHANNEL",
  "PAVE",
  "TENSION",
  "HALO",
  "CLUSTER",
];

export const CERTIFICATION_TYPE_ENUM = [
  "BIS_HALLMARK",
  "GIA",
  "IGI",
  "SGL",
  "HRD",
  "AGS",
  "DGLA",
];

export const CERTIFICATION_AGENCY_ENUM = [
  "BIS",
  "GIA",
  "IGI",
  "SGL",
  "HRD",
  "AGS",
  "DGLA",
];

// ============= DEFAULT STONE (ALIGNED) =============
export const DEFAULT_STONE = {
  type: STONE_TYPE_ENUM[0],
  weight: 0,
  count: 1,
  color: "",
  clarity: "",
  cut: "",
  shape: STONE_SHAPE_ENUM[0],
  settingType: STONE_SETTING_ENUM[0],
  certification: {
    agency: "",
    certificateNumber: "",
    certificateUrl: "",
  },
  ratePerCarat: 0,
  totalValue: 0,
};

// ============= DEFAULT CERTIFICATION (ALIGNED) =============
export const DEFAULT_CERTIFICATION = {
  type: CERTIFICATION_TYPE_ENUM[0],
  number: "",
  issuedBy: "",
  issuedDate: new Date(),
  validUntil: null,
  documentUrl: "",
};

// ============= DEFAULT CUSTOMIZATION OPTION =============
export const DEFAULT_CUSTOMIZATION_OPTION = {
  type: "",
  values: [],
  priceImpact: 0,
};

// ============= DEFAULT PRODUCT DATA (ALIGNED WITH PRODUCTFORM) =============
export const DEFAULT_PRODUCT_DATA = {
  // Basic fields (including new identifiers)
  productId: "", // Auto-generated, read-only in edit mode
  sku: "", // Can be auto-generated or manual
  name: "",
  description: "",
  type: PRODUCT_TYPE_ENUM[0],
  subType: "",
  categories: [],
  collections: [],

  // Weights
  grossWeight: "",
  netWeight: "",

  // Metal details
  purity: "",
  metalColor: METAL_COLOR_ENUM[0],
  metalDetails: {
    purity: "",
    color: METAL_COLOR_ENUM[0],
    fineness: null,
    karat: null,
  },

  // Stone/Diamond Information
  hasStones: false,
  stones: [],
  totalStoneWeight: 0,
  stoneCharges: 0,

  // Manufacturing (with batchNumber)
  karigarCode: "",
  craftType: "",
  technique: "",
  manufacturing: {
    karigarCode: "",
    craftType: "",
    technique: "",
    origin: "",
    productionDate: new Date(),
    batchNumber: "",
  },

  // Stock management - ALIGNED structure
  stock: {
    totalQuantity: 1,
    reservedQuantity: 0,
    availableQuantity: 1,
    minStockLevel: 1,
    maxStockLevel: null,
    locationStock: [], // ALIGNED: inside stock object
  },

  // Branch management
  branchInfo: {
    primaryBranch: "",
    availableBranches: [],
    availableStores: [],
  },

  // Status & Display - ALIGNED: DRAFT as default
  status: PRODUCT_STATUS_ENUM[2], // DRAFT
  viewAccess: ["B2C_WEBSITE", "B2C_APP"],
  gender: "UNISEX",
  occasion: [],
  tags: [],
  isFeatured: false,
  isNewArrival: false,
  isBestseller: false,
  isExclusive: false,
  isPrivate: false,

  // Hallmark - ALIGNED structure
  hallmark: {
    isHallmarked: false,
    huid: "",
    centerCode: "",
    year: new Date().getFullYear().toString(),
    jewellerCode: "",
    purity: "", // ALIGNED: purity not purityMark
  },

  // Certifications
  certifications: [],

  // Size/Variant Information
  sizeInfo: {
    hasSize: false,
    availableSizes: [],
    defaultSize: "",
    sizeChart: "",
  },
  isVariant: false,
  parentProduct: null,
  variants: [],
  variantAttribute: "",

  // Customization Options
  customizable: {
    isCustomizable: false,
    options: [],
  },

  // Pricing config
  pricingConfig: {
    useCustomPricing: false,
    customMakingCharges: null,
    customWastagePercent: null,
    excludeFromOffers: false,
    branchSpecificRules: [],
  },

  // Media
  media: [],

  // SEO reference
  productSeo: null,

  // Legacy fields for backward compatibility
  narration: "",
  isStone: false,
  stoneWeight: 0,
};

// Generate a fresh default product with optional branch
export const generateDefaultProduct = (branchId = "") => {
  return {
    ...DEFAULT_PRODUCT_DATA,
    branchInfo: {
      primaryBranch: branchId,
      availableBranches: branchId ? [branchId] : [],
      availableStores: [],
    },
    stock: {
      ...DEFAULT_PRODUCT_DATA.stock,
      locationStock: [],
    },
    hallmark: {
      ...DEFAULT_PRODUCT_DATA.hallmark,
    },
    manufacturing: {
      ...DEFAULT_PRODUCT_DATA.manufacturing,
    },
    customizable: {
      ...DEFAULT_PRODUCT_DATA.customizable,
    },
    pricingConfig: {
      ...DEFAULT_PRODUCT_DATA.pricingConfig,
    },
    sizeInfo: {
      ...DEFAULT_PRODUCT_DATA.sizeInfo,
    },
  };
};

// Product type configurations with aligned purities
export const PRODUCT_TYPE_CONFIGS = {
  GOLD: {
    label: "Gold",
    purities: ["24K", "22K", "18K", "14K"],
    defaultPurity: "22K",
    colors: ["YELLOW", "WHITE", "ROSE", "TWO_TONE", "THREE_TONE"],
    requiresHallmark: true,
  },
  SILVER: {
    label: "Silver",
    purities: ["925", "950"],
    defaultPurity: "925",
    colors: ["YELLOW"],
    requiresHallmark: true,
  },
  PLATINUM: {
    label: "Platinum",
    purities: ["PT950"],
    defaultPurity: "PT950",
    colors: ["WHITE"],
    requiresHallmark: true,
  },
  DIAMOND: {
    label: "Diamond",
    purities: ["18K", "14K", "PT950"],
    defaultPurity: "18K",
    colors: ["WHITE", "YELLOW", "ROSE"],
    requiresHallmark: true,
  },
  STUDDED: {
    label: "Studded",
    purities: ["22K", "18K", "14K"],
    defaultPurity: "18K",
    colors: ["YELLOW", "WHITE", "ROSE", "TWO_TONE"],
    requiresHallmark: true,
  },
};

// Validate product data - ALIGNED with ProductForm validation
export const validateProduct = (data) => {
  const errors = {};

  // Basic validations - ALWAYS REQUIRED
  if (!data.name || data.name.length < 3) {
    errors.name = "Product name is required (min 3 characters)";
  }
  if (!data.type) {
    errors.type = "Product type is required";
  }
  if (!data.subType) {
    errors.subType = "Product subtype is required";
  }

  // Weight validations
  const grossWeight = parseFloat(data.grossWeight);
  const netWeight = parseFloat(data.netWeight);

  if (isNaN(grossWeight) || grossWeight <= 0) {
    errors.grossWeight = "Gross weight must be greater than 0";
  }
  if (isNaN(netWeight) || netWeight <= 0) {
    errors.netWeight = "Net weight must be greater than 0";
  }
  if (!isNaN(netWeight) && !isNaN(grossWeight) && netWeight > grossWeight) {
    errors.netWeight = "Net weight cannot exceed gross weight";
  }

  // Metal details
  if (!data.purity) {
    errors.purity = "Purity is required";
  }
  if (!data.karigarCode) {
    errors.karigarCode = "Karigar code is required";
  }

  // Hallmark validation - only if enabled
  if (data.hallmark?.isHallmarked) {
    if (!data.hallmark.huid) {
      errors["hallmark.huid"] = "HUID is required for hallmarked products";
    }
    if (!data.hallmark.centerCode) {
      errors["hallmark.centerCode"] = "Center code is required";
    }
  }

  // Stone validation - only if enabled
  if (data.hasStones && (!data.stones || data.stones.length === 0)) {
    errors.stones =
      "At least one stone entry is required when product has stones";
  }

  // Size validation - only if enabled
  if (
    data.sizeInfo?.hasSize &&
    (!data.sizeInfo.availableSizes || data.sizeInfo.availableSizes.length === 0)
  ) {
    errors["sizeInfo.availableSizes"] = "At least one size is required";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Calculate estimated price (placeholder)
export const calculateEstimatedPrice = (data, rates = {}) => {
  // This is a simplified calculation
  // Real calculation would use actual metal rates
  const baseRates = {
    "24K": 6500,
    "22K": 6000,
    "18K": 5000,
    "14K": 4000,
    925: 80,
    950: 85,
    PT950: 3500,
    ...rates,
  };

  const rate = baseRates[data.purity] || 0;
  const netWeight = parseFloat(data.netWeight) || 0;
  const metalValue = netWeight * rate;

  // Add stone charges if any
  const stoneCharges = parseFloat(data.stoneCharges) || 0;

  // Add making charges (10% placeholder)
  const makingCharges = metalValue * 0.1;

  // Add GST (3%)
  const gst = (metalValue + makingCharges + stoneCharges) * 0.03;

  return {
    metalValue: Math.round(metalValue),
    stoneCharges: Math.round(stoneCharges),
    makingCharges: Math.round(makingCharges),
    gst: Math.round(gst),
    total: Math.round(metalValue + makingCharges + stoneCharges + gst),
  };
};

export default {
  DEFAULT_PRODUCT_DATA,
  DEFAULT_STONE,
  DEFAULT_CERTIFICATION,
  DEFAULT_CUSTOMIZATION_OPTION,
  generateDefaultProduct,
  PRODUCT_TYPE_CONFIGS,
  validateProduct,
  calculateEstimatedPrice,
  // Export all enums
  PRODUCT_TYPE_ENUM,
  PRODUCT_STATUS_ENUM,
  PRODUCT_GENDER_ENUM,
  VIEW_ACCESS_ENUM,
  PURITY_ENUM,
  METAL_COLOR_ENUM,
  PRODUCT_SUBTYPE_ENUM,
  CRAFT_TYPE_ENUM,
  TECHNIQUE_ENUM,
  STONE_TYPE_ENUM,
  STONE_SHAPE_ENUM,
  STONE_SETTING_ENUM,
  CERTIFICATION_TYPE_ENUM,
  CERTIFICATION_AGENCY_ENUM,
};
